import { Typography, Box } from "@mui/material"
import { memo, useEffect, useState } from "react";
import {
    getOwners,
    getYearsLambings,
    deleteLambing,
} from "../../api/index.js";
import Spinner from "../../components/Spinner.js";
import { useSnackbar } from "../../utils/index.js";
import Form from "../../components/Form.js";
import useSelectStyles from "../../components/styles/Select.js";
import { BackButton } from "../../components/Buttons.js";

const DeleteLambing = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [yearsDropdownValues, setYearsDropdownValue] = useState([]);
    const [fetchedLambings, setFetchedLambings] = useState({});
    const [animalLambings, setAnimalLambings] = useState([]);
    const [animalLambingsDatesDropdownValues, setAnimalLambingsDatesDropdownValues] = useState([]);
    const [animalsDropdownValues, setAnimalsDropdownValues] = useState([]);
    const [step, setStep] = useState(1);
    /* Step 1: Select owner and year
      Step 2: Show registration codes of female animals that gave birth during the selected year
      Step 3: Show lambings dates of the selected Animal
    */

    const { success, error } = useSnackbar();
    const classes = useSelectStyles();

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);

            const dropdownValues = owners.map((owner) => ({
                value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
            }));
            setOwnersDropdownValues(dropdownValues);
        } catch (err) {
            error(err);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        // Years 2000 to current year
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => ({
            value: (currentYear - i).toString(),
            text: (currentYear - i).toString()
        }));
        setYearsDropdownValue(years);
        fetchData();
    }, []);

    const selectOwnerYearFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Επιλέξτε Εκτροφή",
            required: true,
        },
        {
            customType: "dropdown",
            id: "year",
            items: yearsDropdownValues,
            placeholder: "Επιλέξτε την περίοδο τοκετών",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ];

    const selectAnimalFormContent = [
        {
            customType: "dropdown",
            id: "animal",
            items: animalsDropdownValues,
            placeholder: "Επιλέξτε Ζώο",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ];

    const selectedLambingsFormContent = [
        {
            customType: "dropdown",
            id: "lambingDate",
            items: animalLambingsDatesDropdownValues,
            placeholder: "Επιλέξτε Ημερομηνία Τοκετού",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Διαγραφή Τοκετού"
        },
    ];

    const handleFetchLambings = async (values) => {
        setIsLoading(true);
        const ownerId = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)?._id;
        try {
            const { success: scs, message, lambings } = await getYearsLambings(ownerId, values.year);
            if (scs) {
                setFetchedLambings(lambings);

                const damRegistrationCodes = Object.keys(lambings).filter((code) => code !== "Unknown");

                setAnimalsDropdownValues(damRegistrationCodes.map((damCode) => ({
                    value: damCode,
                    text: damCode,
                })));
                setStep(2);
            } else {
                error(message);
            }
        } catch (err) {
            error(err);
        } finally {
            setIsLoading(false);
        }
    }

    const handleAnimalSelect = (values) => {
        const selectedLambings = fetchedLambings[values.animal] || [];

        if (selectedLambings.length > 0) {
            setAnimalLambings(selectedLambings);

            const datesDropdownValues = selectedLambings.map((lambing) => ({
                value: new Date(lambing?.lambingDate).toLocaleDateString("el-GR"),
                text: new Date(lambing?.lambingDate).toLocaleDateString("el-GR"),
            }));

            setAnimalLambingsDatesDropdownValues(datesDropdownValues);
            setStep(3);
        }
    };

    const handleDeleteLambing = async (values) => {
        setIsLoading(true);

        const lambingToDelete = animalLambings.find((lambing) => new Date(lambing?.lambingDate).toLocaleDateString("el-GR") === values.lambingDate);

        try {
            const { success: scs, message } = await deleteLambing(lambingToDelete?.id);
            if (scs) {
                success(message);
                setStep(1);
                setAnimalLambings([]);
                setAnimalLambingsDatesDropdownValues([]);
                setAnimalsDropdownValues([]);
                setFetchedLambings({});
            } else {
                error(message);
            }
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />

            <Box className={classes.outerBox}>
                <Box container flexDirection="column" className={classes.innerBox}>
                    {step === 1 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλέξτε την Εκτροφή και την περίοδο τοκετών που σας ενδιαφέρει"}
                            </Typography>
                            <Form
                                title="Διαγραφή Τοκετών"
                                content={selectOwnerYearFormContent}
                                onSubmit={(values) => handleFetchLambings(values)}
                                toResetForm={true}
                            />
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλέξτε το Ζώο που σας ενδιαφέρει"}
                            </Typography>
                            <Form
                                title="Επιλογή Προβατίνας"
                                content={selectAnimalFormContent}
                                onSubmit={(values) => handleAnimalSelect(values)}
                                toResetForm={true}
                            />
                        </>
                    )}
                    {step === 3 && (
                        <>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλέξτε την ημερομηνία τοκετού που θέλετε να διαγράψετε"}
                            </Typography>
                            <Form
                                title="Επιλογή Τοκετού"
                                content={selectedLambingsFormContent}
                                onSubmit={(values) => handleDeleteLambing(values)}
                                toResetForm={true}
                            />
                        </>
                    )}
                    {step !== 1 && (
                        <BackButton onClick={() => setStep(step - 1)} />
                    )}
                </Box>
            </Box>
        </>
    )
};

export default memo(DeleteLambing);